import Checkbox from '@mui/material/Checkbox';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../shared/helpers';
import { useLocation } from '../../../contexts/RouterContext';
import { useIsQualityEnabled } from '../../../helpers/features';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import Badge, { BadgeColors, BadgeShapes } from '../../generic/Badge/Badge';
import Button from '../../generic/Button';
import FallbackImage from '../../generic/FallbackImage';
import List from '../../generic/List';
import type { PackageDetails } from '../../../../shared/organizationPackages';

import PackageDetailsTableRow from './PackageDetailsTableRow';

type Props = {
  packageLocator?: string;
  onShowBlockModal: () => void;
};

type PackageDetailsTableBodyProps = Omit<Props, 'onShowBlockModal'> & {
  isLoading: boolean;
  packageDetails: PackageDetails[] | null;
};

/**
 * Inner body part of the table.
 */
const PackageDetailsTableBody: VoidFunctionComponent<PackageDetailsTableBodyProps> = ({
  packageLocator,
  isLoading,
  packageDetails,
}) => {
  const location = useLocation();

  if (isLoading || !packageLocator) {
    return _.times(3, () => <List.RowLoading />);
  }

  if (!packageDetails || packageDetails?.length === 0) {
    return (
      <FallbackImage src="no-results" alt="No versions found!" hasPadding>
        No versions found!
      </FallbackImage>
    );
  }

  return packageDetails.map((details) => (
    <PackageDetailsTableRow
      key={details.packageRevision}
      pathname={location.pathname}
      {...details}
    />
  ));
};

/**
 * Renders the list of versions for a given package.
 *
 * If the package denial workflow is enabled, then this will also
 * display checkboxes next to each version, allowing the user
 * to select specific versions of this package to deny.
 */
const PackageDetailsTable: VoidFunctionComponent<Props> = ({ packageLocator, onShowBlockModal }) => {
  const dispatch = useDispatch();

  const isLoading = useSelector(PackageInventoryStore.selectors.getPackageDetailsPageLoading);
  const packageDetails = useSelector(PackageInventoryStore.selectors.getPackageDetails);

  const areAllVersionsSelected = useSelector(PackageInventoryStore.selectors.getAreAllVersionsSelected);
  const areSomeVersionsSelected = useSelector(PackageInventoryStore.selectors.getAreSomeVersionsSelected);
  const selectedVersionsCount = useSelector(PackageInventoryStore.selectors.getSelectedVersionsCount);

  const hasQuality = useIsQualityEnabled();

  return (
    <div className="package-inventory__details__table">
      <List className="package-inventory__details__table__list">
        <List.Header hasCheckbox={hasQuality}>
          {hasQuality && (
            <Checkbox
              checked={areAllVersionsSelected}
              indeterminate={!areAllVersionsSelected && areSomeVersionsSelected}
              onChange={() => dispatch(PackageInventoryStore.actions.toggleSelectAllVersions())}
              disabled={isLoading}
              color="primary"
              disableRipple
              disableFocusRipple
              disableTouchRipple
            />
          )}
          {areSomeVersionsSelected ? (
            <Button
              type="secondary"
              size="small"
              onClick={onShowBlockModal}
            >
              Block {plural(selectedVersionsCount, 'version')}
            </Button>
          ) : (
            <>
              <List.HeaderText>Versions</List.HeaderText>
              <span className="inline-block margin-left-xs">
                <Badge
                  shape={BadgeShapes.Circle}
                  color={BadgeColors.LightGray}
                >
                  {isLoading || !packageDetails ? '...' : packageDetails.length.toString()}
                </Badge>
              </span>
            </>
          )}
        </List.Header>
        <List.Body>
          <PackageDetailsTableBody
            packageLocator={packageLocator}
            isLoading={isLoading}
            packageDetails={packageDetails}
          />
        </List.Body>
      </List>
    </div>
  );
};

export default PackageDetailsTable;
